import type { ApiFile, ApiReference } from "../shared/interfaces";
import { getReferenceDescription, getReferenceText } from "./referencePolicy";

const REFERENCE_PATTERN = /(`?)\{(\d+(?:,\s*\d+)*)}\1|(`?)\[(\d+(?:,\s*\d+)*)]\3/g;
const REFERENCE_TOKEN_RE = /\{(\d+(?:,\s*\d+)*)\}/g;

export function convertReferencesToMarkdown(input: string): string {
    const parts: Array<{ reference: boolean; content: string }> = [];
    let lastIndex = 0;
    input.replace(
        REFERENCE_PATTERN,
        (match, _curlyTicks, curlyRefs, _squareTicks, squareRefs, offset: number) => {
            if (offset > lastIndex) {
                parts.push({ reference: false, content: input.slice(lastIndex, offset) });
            }
            parts.push({
                reference: true,
                content: String(curlyRefs ?? squareRefs)
                    .split(",")
                    .map((key) => key.trim())
                    .join(","),
            });
            lastIndex = offset + match.length;
            return match;
        },
    );
    if (lastIndex < input.length) {
        parts.push({ reference: false, content: input.slice(lastIndex) });
    }

    const result: string[] = [];
    for (let index = 0; index < parts.length; ) {
        if (!parts[index].reference) {
            result.push(parts[index].content);
            index += 1;
            continue;
        }
        const keys: string[] = [];
        while (index < parts.length && parts[index].reference) {
            keys.push(parts[index].content);
            index += 1;
        }
        result.push(`[${keys.join(",")}](#REFERENCEGROUP)`);
    }
    return result.join("");
}

export class ConversationReferenceProcessor {
    private readonly references: Map<string, ApiReference>;
    private readonly files: Map<string, ApiFile>;

    constructor(references: Map<string, ApiReference>, files: ApiFile[]) {
        this.references = references;
        this.files = new Map(files.map((file) => [file.id, file]));
    }

    isReferenceValid(key: string): boolean {
        const reference = this.references.get(key);
        return Boolean(reference && this.files.has(reference.fileId));
    }

    getReferenceOrNull(key: string): ApiReference | null {
        return this.references.get(key) ?? null;
    }

    getText(key: string, maxLength = 40): string {
        const reference = this.references.get(key);
        if (!reference) return "Reference";
        const file = this.files.get(reference.fileId);
        return file ? getReferenceText(reference, file, maxLength) : "Reference";
    }

    getDescription(key: string): string {
        const reference = this.references.get(key);
        if (!reference) return "";
        const file = this.files.get(reference.fileId);
        return file ? getReferenceDescription(reference, file) : "";
    }
}

export function stripBold(text: string): string {
    return text.replace(/\*\*/g, "");
}

export function resolveThinkingReferences(
    text: string,
    processor: ConversationReferenceProcessor,
): string {
    return text.replace(REFERENCE_TOKEN_RE, (match, ids: string) => {
        const labels = ids
            .split(/\s*,\s*/)
            .filter((key) => processor.isReferenceValid(key))
            .map((key) => processor.getText(key, 30));
        return labels.length > 0 ? labels.join(", ") : match;
    });
}
